import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CardinalIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LaptopIcon from '@mui/icons-material/Laptop';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import agent from '../../agent';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const TYPE_META = {
  RECOGNITION: { icon: <VisibilityIcon sx={{ fontSize: 20 }} />, color: DEFAULT_THEME_COLOR, bg: 'rgba(54,201,162,0.12)' },
  PHYSICAL:    { icon: <CardGiftcardIcon sx={{ fontSize: 20 }} />, color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
  DIGITAL:     { icon: <LaptopIcon sx={{ fontSize: 20 }} />, color: '#4facfe', bg: 'rgba(79,172,254,0.12)' },
  DISCOUNT:    { icon: <LocalOfferIcon sx={{ fontSize: 20 }} />, color: '#f06292', bg: 'rgba(240,98,146,0.12)' },
  EXPERIENCE:  { icon: <StarIcon sx={{ fontSize: 20 }} />, color: '#ab47bc', bg: 'rgba(171,71,188,0.12)' },
};

const typeLabel = (type, t) => {
  const map = {
    RECOGNITION: t.profile.giftTypeRecognition,
    PHYSICAL:    t.profile.giftTypePhysical,
    DIGITAL:     t.profile.giftTypeDigital,
    DISCOUNT:    t.profile.giftTypeDiscount,
    EXPERIENCE:  t.profile.giftTypeExperience,
  };
  return map[type] || type;
};

const GiftCard = ({ gift, t }) => {
  const meta = TYPE_META[gift.type] || TYPE_META.RECOGNITION;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
        border: '1px solid rgba(255,200,50,0.18)',
        borderRadius: 3,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${meta.color}88, transparent)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            background: meta.bg,
            border: `1px solid ${meta.color}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            color: meta.color,
          }}
        >
          {meta.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}
          >
            {gift.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              size="small"
              label={typeLabel(gift.type, t)}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                background: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.color}44`,
              }}
            />
            {gift.value && (
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,200,50,0.7)', fontWeight: 600 }}>
                {gift.value}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.855rem', lineHeight: 1.6 }}>
        {gift.description}
      </Typography>

      <Box
        sx={{
          background: 'rgba(54,201,162,0.06)',
          border: '1px solid rgba(54,201,162,0.18)',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14, color: DEFAULT_THEME_COLOR, mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: DEFAULT_THEME_COLOR, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t.profile.howToGain}
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
            {gift.howToGain}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const AmbassadorGiftsComponent = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = useTranslation();

  useEffect(() => {
    agent.AmbassadorGifts.get()
      .then(res => setGifts(res?.gifts ?? []))
      .catch(() => setGifts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || gifts.length === 0) return null;

  const universal = gifts.filter(g => !g.ambassadorContext);
  const contextual = gifts.filter(g => g.ambassadorContext);

  return (
    <Box sx={{ px: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <EmojiEventsIcon sx={{ color: 'gold', fontSize: 18 }} />
        <Typography
          variant="caption"
          sx={{ color: 'gold', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {t.profile.ambassadorGifts}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', mb: 2, lineHeight: 1.5 }}>
        {t.profile.ambassadorGiftsSubline}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {contextual.length > 0 && contextual.map(g => <GiftCard key={g.id} gift={g} t={t} />)}
        {universal.map(g => <GiftCard key={g.id} gift={g} t={t} />)}
      </Box>
    </Box>
  );
};

export default AmbassadorGiftsComponent;
